import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import TopHeaderDerin, { TabType } from "../../components/derin/TopHeaderDerin";
import OrganizationsSection from "../../components/derin/OrganizationsSection";
import FeedbackSection from "../../components/derin/FeedbackSection";
import MigrationSection from "../../components/derin/MigrationSection";
import UsersSection from "../../components/derin/Users/UsersSection";
import { useAppSelector } from "../../store/reduxHooks";

import { DerinOrganization, DerinUser, getOrganizations, getUsers, getGroupedFeedbackSessions } from "../../api/derin";

/**
 * Who sees the surveys → collections migration tab. It is a one-off backfill, so it stays
 * out of everyone else's way. Note this only hides the UI, it does not protect the route:
 * this portal authenticates against Mosaic and the Derin backend cannot verify that token,
 * so there is no server-side check to gate it with.
 */
const MIGRATION_ADMIN_EMAILS = ["lucasgomesantos@surgohealth.com"];

const AdminControlsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("organizations");
  const currentUser = useAppSelector((state) => state.userSlice.user);
  const canMigrate = MIGRATION_ADMIN_EMAILS.includes(
    (currentUser?.email ?? "").trim().toLowerCase()
  );
  const [organizations, setOrganizations] = useState<DerinOrganization[]>([]);
  const [users, setUsers] = useState<DerinUser[]>([]);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [orgsData, usersData, groupedData] = await Promise.all([
        getOrganizations(),
        getUsers(),
        getGroupedFeedbackSessions({ limit: 100 }),
      ]);
      setOrganizations(orgsData);
      setUsers(usersData);
      setFeedbackCount(groupedData.pagination.totalCount);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  const handleRefresh = () => {
    fetchData();
  };

  /**
   * The Users tab only changes users, so it refetches just them. A full refresh
   * also reloads the grouped feedback listing, which takes several seconds.
   */
  const refreshUsers = async () => {
    try {
      setUsers(await getUsers());
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  return (
    <Box>
        <TopHeaderDerin
          activeTab={activeTab}
          onTabChange={handleTabChange}
          stats={{
            organizationsCount: organizations.length,
            usersCount: users.length,
          }}
          feedbackCount={feedbackCount}
          showMigrationTab={canMigrate}
        />

        {activeTab === "organizations" && (
          <OrganizationsSection
            organizations={organizations}
            isLoading={isLoading}
            onRefresh={handleRefresh}
          />
        )}
        {activeTab === "feedback" && <FeedbackSection />}
        {activeTab === "users" && (
          <UsersSection
            users={users}
            organizations={organizations}
            isLoading={isLoading}
            onRefresh={refreshUsers}
          />
        )}
        {/* Guarded here as well as in the tab list, so the section cannot render for a
            non-admin even if activeTab somehow becomes "migration". */}
        {activeTab === "migration" && canMigrate && <MigrationSection />}
      </Box>
  );
};

export default AdminControlsPage;
